import type { Client } from 'appwrite';

export const IMPERSONATE_USER_HEADER = 'X-Appwrite-Impersonate-User-Id';
const STORAGE_TARGET_KEY = 'impersonate-user-id';
const STORAGE_OPERATOR_COMPANY_KEY = 'impersonate-operator-company-id';

let appwriteClient: Client | null = null;

export function bindImpersonationClient(client: Client): void {
  appwriteClient = client;
  restoreImpersonationHeader();
}

function getClient(): Client {
  if (!appwriteClient) {
    throw new Error('Appwrite client not bound for impersonation');
  }
  return appwriteClient;
}

function readSessionItem(key: string): string | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  return sessionStorage.getItem(key);
}

export function getImpersonationTargetUserId(): string | null {
  return readSessionItem(STORAGE_TARGET_KEY);
}

export function getOperatorCompanyId(): string | null {
  return readSessionItem(STORAGE_OPERATOR_COMPANY_KEY);
}

export function restoreImpersonationHeader(): void {
  if (!appwriteClient) {
    return;
  }

  const targetUserId = getImpersonationTargetUserId();
  if (targetUserId) {
    appwriteClient.headers[IMPERSONATE_USER_HEADER] = targetUserId;
  } else {
    delete appwriteClient.headers[IMPERSONATE_USER_HEADER];
  }
}

export function setImpersonationTarget(userId: string, operatorCompanyId: string | null): void {
  getClient().headers[IMPERSONATE_USER_HEADER] = userId;
  sessionStorage.setItem(STORAGE_TARGET_KEY, userId);
  if (operatorCompanyId) {
    sessionStorage.setItem(STORAGE_OPERATOR_COMPANY_KEY, operatorCompanyId);
  } else {
    sessionStorage.removeItem(STORAGE_OPERATOR_COMPANY_KEY);
  }
}

export function clearImpersonationTarget(): void {
  delete getClient().headers[IMPERSONATE_USER_HEADER];
  sessionStorage.removeItem(STORAGE_TARGET_KEY);
  sessionStorage.removeItem(STORAGE_OPERATOR_COMPANY_KEY);
}

export async function withOperatorContext<T>(fn: () => Promise<T>): Promise<T> {
  if (!appwriteClient || !getImpersonationTargetUserId()) {
    return fn();
  }

  delete appwriteClient.headers[IMPERSONATE_USER_HEADER];
  try {
    return await fn();
  } finally {
    restoreImpersonationHeader();
  }
}
